const focusSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
const animations = new WeakMap();
const scrollOwners = new Set();
let scrollSnapshot = null;

export const isVisible = (element) =>
    Boolean(
        element &&
        !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
        element.getClientRects().length &&
        getComputedStyle(element).visibility !== 'hidden'
    );

export const focusableElements = (scope) =>
    Array.from(scope.querySelectorAll(focusSelector)).filter(isVisible);

export function focusElement(element) {
    if (!isVisible(element)) {
        return false;
    }

    element.focus({ preventScroll: true });
    return document.activeElement === element;
}

export function trapFocus(event, scope) {
    if (event.key !== 'Tab') {
        return;
    }

    const elements = focusableElements(scope);
    const first = elements[0];
    const last = elements[elements.length - 1];

    if (!first) {
        event.preventDefault();
        return;
    }

    if (
        event.shiftKey &&
        (document.activeElement === first || !elements.includes(document.activeElement))
    ) {
        event.preventDefault();
        focusElement(last);
    } else if (
        !event.shiftKey &&
        (document.activeElement === last || !elements.includes(document.activeElement))
    ) {
        event.preventDefault();
        focusElement(first);
    }
}

export function setPanelVisibility(element, open, { immediate = false, distance = 8 } = {}) {
    if (!element) {
        return;
    }

    const wasHidden = element.hidden;
    const opacity = wasHidden ? 0 : Number(getComputedStyle(element).opacity);
    animations.get(element)?.cancel();
    element.dataset.open = String(open);
    element.inert = !open;
    element.setAttribute('aria-hidden', String(!open));
    element.hidden = false;

    if (
        immediate ||
        !element.animate ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        element.hidden = !open;
        return;
    }

    const animation = element.animate(
        [
            { opacity, transform: `translateY(${open && wasHidden ? distance : 0}px)` },
            { opacity: open ? 1 : 0, transform: `translateY(${open ? 0 : distance}px)` },
        ],
        {
            duration: 180,
            easing: 'ease-out',
        }
    );

    animations.set(element, animation);
    animation.finished
        .then(() => {
            if (animations.get(element) === animation) {
                element.hidden = !open;
                animations.delete(element);
            }
        })
        .catch(() => { });
}

export function inertOutside(scope) {
    const previous = new Map();
    let branch = scope;

    while (branch && branch !== document.body) {
        Array.from(branch.parentElement?.children || []).forEach((sibling) => {
            if (sibling !== branch && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
                previous.set(sibling, sibling.inert);
                sibling.inert = true;
            }
        });
        branch = branch.parentElement;
    }

    return () => {
        previous.forEach((inert, element) => {
            element.inert = inert;
        });
    };
}

export function lockPage(owner) {
    if (scrollOwners.has(owner)) {
        return;
    }

    scrollOwners.add(owner);

    if (scrollSnapshot) {
        return;
    }

    const body = document.body;
    const html = document.documentElement;
    const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight'];
    scrollSnapshot = {
        x: window.scrollX,
        y: window.scrollY,
        styles: Object.fromEntries(properties.map((property) => [property, body.style[property]])),
        htmlOverflow: html.style.overflow,
        blockScroll: body.classList.contains('block-scroll'),
    };
    const gutter = window.innerWidth - html.clientWidth;
    const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.position = 'fixed';
    body.style.top = `${-scrollSnapshot.y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.paddingRight = `${padding + gutter}px`;
    html.style.overflow = 'hidden';
    body.classList.add('block-scroll');
}

export function unlockPage(owner) {
    scrollOwners.delete(owner);

    if (scrollOwners.size || !scrollSnapshot) {
        return;
    }

    const snapshot = scrollSnapshot;
    scrollSnapshot = null;
    Object.assign(document.body.style, snapshot.styles);
    document.documentElement.style.overflow = snapshot.htmlOverflow;
    document.body.classList.toggle('block-scroll', snapshot.blockScroll);
    const scrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(snapshot.x, snapshot.y);
    document.documentElement.style.scrollBehavior = scrollBehavior;
}

export const pageScrollY = () => scrollSnapshot?.y ?? window.scrollY;
